import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import authService from '../../services/auth';
import PublicShell from '../../components/PublicShell/PublicShell';
import deviceImg from '../../assets/landing/NissaMeter.jpeg';
import './Login.css';

/* Lo que el equipo hace de verdad, en el orden en que le importa a quien
   entra. Sin promesas que el producto no cumpla. */
const facts = [
  { value: 'Cada 30 s', label: 'Lectura de consumo y presión' },
  { value: 'Corte remoto', label: 'Apertura y cierre de la válvula' },
  { value: 'Historial', label: 'Consumo, costo estimado y alertas' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Si ya está autenticado, redirigir al home
    if (authService.isAuthenticated()) {
      navigate('/home');
      return;
    }

    // Manejar mensajes desde otras pantallas
    if (location.state?.message) {
      setSuccess(location.state.message);
    }

    // Pre-llenar email si viene desde otra pantalla
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await authService.login(email, password);
      navigate('/home');
    } catch (err) {
      console.error('Error en login:', err);

      let errorMessage = 'Error al iniciar sesión';

      if (err.code === 'UserNotFoundException') {
        errorMessage = 'Usuario no encontrado';
      } else if (err.code === 'NotAuthorizedException') {
        errorMessage = 'Contraseña incorrecta';
      } else if (err.code === 'UserNotConfirmedException') {
        errorMessage = 'Email no verificado. Revisa tu correo para confirmar tu cuenta.';
        // Redirigir a confirmación de email
        setTimeout(() => {
          navigate('/confirm-email', { state: { email } });
        }, 2000);
      } else if (err.code === 'NewPasswordRequired') {
        // Redirigir a cambio de contraseña
        navigate('/new-password-required');
        return; // No mostrar error, es un flujo normal
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicShell>
      <div className="auth-login">
        {/* Zona principal: qué es NisaGuard y qué se ve al entrar. */}
        <section className="auth-pitch">
          <h1 className="auth-pitch__title">
            Tu instalación de agua, medida y bajo control.
          </h1>
          <p className="auth-pitch__body">
            NisaMeter mide el consumo en tiempo real, avisa cuando algo se sale
            de lo normal y corta el paso a distancia.
          </p>

          <figure className="auth-pitch__figure">
            <img
              src={deviceImg}
              alt="Instalación de NisaMeter sobre la tubería principal, con electroválvula de corte"
              className="auth-pitch__photo"
              loading="lazy"
            />
            <figcaption className="auth-pitch__caption">
              Medición y válvula de corte instaladas en línea sobre la toma principal.
            </figcaption>
          </figure>

          <div className="auth-facts">
            {facts.map(({ value, label }) => (
              <div className="auth-facts__item" key={value}>
                <span className="auth-facts__value">{value}</span>
                <span className="auth-facts__label">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Zona de acceso. */}
        <section className="auth-login__form">
          <div className="auth-card">
            <div className="auth-card__head">
              <h2 className="auth-card__title">Iniciar sesión</h2>
              <p className="auth-card__sub">
                Entra con la cuenta asociada a tus dispositivos.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Correo electrónico</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Contraseña</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Tu contraseña"
                  autoComplete="current-password"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="error-message" role="alert">
                  {error}
                </div>
              )}

              {success && (
                <div className="success-message" role="status">
                  {success}
                </div>
              )}

              <button
                type="submit"
                className="ng-btn ng-btn--primary ng-btn--lg ng-btn--block"
                data-loading={loading ? 'true' : undefined}
                disabled={loading}
              >
                {loading ? 'Iniciando sesión' : 'Iniciar sesión'}
              </button>

              {/* La demostración ya NO tiene botón propio. Se entra por este mismo
                  formulario, con las credenciales de `demoSession.js`, que
                  `authService.login()` intercepta antes de llegar a Cognito. Así el
                  modo de prueba deja de estar a un clic para cualquiera que abra la
                  aplicación, sin necesidad de una pantalla aparte. */}

              <div className="auth-card__foot">
                <p>
                  ¿No tienes cuenta? <Link to="/register">Crear cuenta</Link>
                </p>
              </div>
            </form>
          </div>
        </section>
      </div>
    </PublicShell>
  );
};

export default Login;
